import React from 'react';

import { useRef, useState, useEffect } from "react";
import { Checkbox, Panel, DefaultButton, TextField, SpinButton, Dropdown, IDropdownOption } from "@fluentui/react";

import styles from "./Chat.module.css";

import { chatApi, update, userFeedback, endConversationApi, RetrievalMode, Approaches, AskResponse, ChatRequest, ChatTurn, consentApi, revokeConsentApi } from "../../api";
import { Answer, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { mergeStyleSets, FocusTrapZone, Layer, Overlay, Popup } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';

import { TiWarning } from "react-icons/ti";
import { MdOutlineInfo, MdPrivacyTip } from "react-icons/md";
import { BiLike, BiDislike } from "react-icons/bi";
import { IoSend } from "react-icons/io5";

import logo from "../../../public/Ask-Jai-Icon.svg";
import chatbotLogo from "../../../public/Chatbot.png";
import headerLogo from "../../../public/Ask_Jai-Header-Logo.svg";
import triangle from "../../../public/triangle.svg";
import headerImage from "../../../public/header-image.svg";


const popupStyles = mergeStyleSets({
    root: {
        background: 'rgba(0, 0, 0, 0.3)',
        bottom: '0',
        left: '0',
        position: 'fixed',
        right: '0',
        top: '0',
    },
    content: {
        background: 'white',
        left: '50%',
        maxWidth: '400px',
        padding: '20px',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
    },
});

const Chat = () => {
    const [isChatbotOpen, setIsChatbotOpen] = useState<boolean>(false);
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const [isPopupVisible, { setTrue: showPopup, setFalse: hidePopup }] = useBoolean(false);
    const [isCloseConfirmVisible, { setTrue: showCloseConfirm, setFalse: hideCloseConfirm }] = useBoolean(false);
    const [isPrivacyPolicyVisible, { setTrue: showPrivacyPolicy, setFalse: hidePrivacyPolicy }] = useBoolean(false);
    const [hasAgreedToPolicy, setHasAgreedToPolicy] = useState<boolean>(false);
    const [hasAgreedToPrivacyNotice, setHasAgreedToPrivacyNotice] = useState<boolean>(false);
    const [comment, setComment] = useState<string>("");
    const likeVal = useRef<{ val: number, index: number }>({ val: 0, index: 0 });
    const [commentError, setCommentError] = useState<string>("");

    const toggleChatbot = () => {
        if (isChatbotOpen) {
            // Show confirmation popup if there are messages
            if (answers.length > 0) {
                showCloseConfirm();
            } else {
                // No messages, close directly
                closeChatbot();
            }
        } else {
            // Open chatbot and show Privacy Notice if not agreed
            setIsChatbotOpen(true);
            if (!hasAgreedToPolicy) {
                showPrivacyPolicy();
            }
        }
    };

    const handlePrivacyPolicyAgree = () => {
        if (!hasAgreedToPrivacyNotice) return;
        setHasAgreedToPolicy(true);
        hidePrivacyPolicy();
    };

    const closeChatbot = () => {
        // Start closing animation
        setIsClosing(true);
        // Wait for animation to complete before actually closing
        setTimeout(() => {
            setIsChatbotOpen(false);
            setIsClosing(false);
            setHasAgreedToPolicy(false);
            setHasAgreedToPrivacyNotice(false);
            setMarketingConsent(false);
            setWithdrawConsentOnClose(false);
            setDeleteDataOnClose(false);
            setShowMarketingConsentModal(false);
            setMarketingConsentChecked(false);
            setHasHandledConsentThisConversation(false);
        }, 500); // Match the CSS animation duration (0.5s = 500ms)
    };

    const handleEndConversation = () => {
        // Call the end conversation API without waiting (fire and forget)
        if (authToken && conversationId) {
            if (withdrawConsentOnClose || deleteDataOnClose) {
                revokeConsentApi(conversationId, withdrawConsentOnClose, deleteDataOnClose, authToken).catch(error => {
                    console.error("Error revoking marketing consent:", error);
                });
            }

            endConversationApi(conversationId, authToken).catch(error => {
                console.error("Error ending conversation:", error);
            });
        }

        // Clear the chat
        clearChat();

        // Close the confirmation popup
        hideCloseConfirm();

        // Close the chatbot immediately without waiting
        closeChatbot();
    };

    // Function to handle inactivity timeout
    const handleInactivityTimeout = () => {
        console.log("User inactive for 2 minutes, ending conversation");

        // Call the end conversation API if there's an active conversation
        if (authToken && conversationId && answers.length > 0) {
            endConversationApi(conversationId, authToken).catch(error => {
                console.error("Error ending conversation due to inactivity:", error);
            });
        }

        // Clear the chat and close chatbot
        clearChat();
        closeChatbot();
    };

    // Function to reset inactivity timer
    const resetInactivityTimer = () => {
        // Clear existing timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        // Only set timer if chatbot is open and there are messages
        if (isChatbotOpen && answers.length > 0) {
            inactivityTimerRef.current = setTimeout(handleInactivityTimeout, INACTIVITY_TIMEOUT);
        }
    };

    // Function to clear inactivity timer
    const clearInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    };

    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Vectors);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(true);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);

    const lastQuestionRef = useRef<string>("");
    const latestMessageRef = useRef<HTMLDivElement | null>(null);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: AskResponse, userTimestamp: Date][]>([]);
    const [currentUserMessageTimestamp, setCurrentUserMessageTimestamp] = useState<Date | null>(null);

    // State to store the authentication token
    const [authToken, setAuthToken] = useState<string | null>(null);
    const [isTokenExpired, setIsTokenExpired] = useState<boolean>(false);
    const [authRetryCount, setAuthRetryCount] = useState<number>(0);
    const [isUnauthorizedBlocked, setIsUnauthorizedBlocked] = useState<boolean>(false);
    const [sessionEndedMessage, setSessionEndedMessage] = useState<string>("");
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false);
    const [withdrawConsentOnClose, setWithdrawConsentOnClose] = useState<boolean>(false);
    const [deleteDataOnClose, setDeleteDataOnClose] = useState<boolean>(false);

    // Marketing consent modal state (triggered by request_consent: true in API response)
    const [showMarketingConsentModal, setShowMarketingConsentModal] = useState<boolean>(false);
    const [marketingConsentChecked, setMarketingConsentChecked] = useState<boolean>(false);
    const [isConsentLoading, setIsConsentLoading] = useState<boolean>(false);
    const [hasHandledConsentThisConversation, setHasHandledConsentThisConversation] = useState<boolean>(false);

    // State for feedback success toast
    const [showFeedbackToast, setShowFeedbackToast] = useState<boolean>(false);
    const [feedbackToastMessage, setFeedbackToastMessage] = useState<string>("");

    // Inactivity timer state
    const inactivityTimerRef = useRef<number | null>(null);
    const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

    // Reset marketing consent if privacy notice is unchecked
    useEffect(() => {
        if (!hasAgreedToPrivacyNotice) {
            setMarketingConsent(false);
        }
    }, [hasAgreedToPrivacyNotice]);

    useEffect(() => {
        if (!isChatbotOpen) {
            setAuthToken(null);
            return;
        }

        // Only initialize if chatbot is open AND user has agreed to Privacy Notice
        if (!hasAgreedToPolicy) {
            return;
        }

        // Only initialize if chatbot is open
        const initializeChatbot = async () => {
            try {
                // Set default username
                localStorage.setItem("username", "localhost");

                const isDev = import.meta.env.DEV;
                const authBaseUrl = import.meta.env.VITE_MAIN_API;

                // Step 1: Call the challenge API
                const challengeUrl = isDev ? "/chat/challenge" : `${authBaseUrl}/chat/challenge`;
                const challengeResponse = await fetch(challengeUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });

                if (!challengeResponse.ok) {
                    throw new Error(`Challenge API failed with status: ${challengeResponse.status} `);
                }

                const challengeData = await challengeResponse.json();

                const challenge = challengeData.challenge;
                const expiresIn = challengeData.expires_in;

                if (!challenge) {
                    throw new Error("No challenge received from API");
                }

                // Step 2: Immediately call the handshake API with the challenge

                const handshakeUrl = isDev ? "/chat/handshake" : `${authBaseUrl}/chat/handshake`;
                const handshakeResponse = await fetch(handshakeUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Challenge": challenge
                    }
                });

                if (!handshakeResponse.ok) {
                    throw new Error(`Handshake API failed with status: ${handshakeResponse.status} `);
                }

                const handshakeData = await handshakeResponse.json();

                // Store the token in state
                if (handshakeData.data.token) {
                    setAuthToken(handshakeData.data.token);
                } else {
                    console.warn("No token received from handshake API");
                }

            } catch (error) {
                console.error("Error during chatbot initialization:", error);
                setAuthToken(null);
            }
        };

        // Call the initialization function when chatbot opens
        initializeChatbot();

    }, [isChatbotOpen, hasAgreedToPolicy]); // Run when isChatbotOpen or hasAgreedToPolicy changes

    // Handle browser tab close - call end conversation API
    useEffect(() => {
        const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
            if (isChatbotOpen && authToken && conversationId && answers.length > 0) {
                // Call the end conversation API
                try {
                    // Use sendBeacon for reliable delivery during page unload
                    const isDev = import.meta.env.DEV;
                    const baseUrl = isDev ? "" : import.meta.env.VITE_CHATBOT_API;
                    const url = `${baseUrl}/api/v1/conversation/end`;

                    const blob = new Blob([JSON.stringify({ conversation_id: conversationId })], { type: 'application/json' });
                    navigator.sendBeacon(url, blob);
                } catch (error) {
                    console.error("Error calling end conversation API on tab close:", error);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isChatbotOpen, authToken, conversationId, answers.length]);

    // Inactivity timer effect - start timer when there are messages
    useEffect(() => {
        if (isChatbotOpen && answers.length > 0) {
            resetInactivityTimer();
        } else {
            clearInactivityTimer();
        }

        // Cleanup timer on unmount or when chatbot closes
        return () => {
            clearInactivityTimer();
        };
    }, [isChatbotOpen, answers.length]);

    // User activity detection effect
    useEffect(() => {
        if (!isChatbotOpen || answers.length === 0) {
            return;
        }

        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        const handleUserActivity = () => {
            resetInactivityTimer();
        };

        // Add event listeners for user activity
        activityEvents.forEach(event => {
            document.addEventListener(event, handleUserActivity, true);
        });

        // Cleanup event listeners
        return () => {
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true);
            });
        };
    }, [isChatbotOpen, answers.length]);

    // Page visibility change effect - handle browser tab switching
    useEffect(() => {
        if (!isChatbotOpen || answers.length === 0) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden (user switched tabs or minimized browser)
                clearInactivityTimer();
            } else {
                // Page is visible again, reset timer
                resetInactivityTimer();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isChatbotOpen, answers.length]);

    // Function to refresh token when it expires
    const refreshToken = async () => {

        try {
            if (authToken && conversationId) {
                await endConversationApi(conversationId, authToken);
            }
        } catch (error) {
            console.error("Error ending conversation:", error);
        }

        try {

            setIsTokenExpired(false);
            setIsLoading(true);

            const isDev = import.meta.env.DEV;
            const authBaseUrl = import.meta.env.VITE_MAIN_API;

            // Step 1: Call the challenge API
            const challengeUrl = isDev ? "/chat/challenge" : `${authBaseUrl}/chat/challenge`;
            const challengeResponse = await fetch(challengeUrl, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            if (!challengeResponse.ok) {
                throw new Error(`Challenge API failed with status: ${challengeResponse.status} `);
            }

            const challengeData = await challengeResponse.json();
            const challenge = challengeData.challenge;

            if (!challenge) {
                throw new Error("No challenge received from API");
            }

            // Step 2: Call the handshake API with the challenge
            const handshakeUrl = isDev ? "/chat/handshake" : `${authBaseUrl}/chat/handshake`;
            const handshakeResponse = await fetch(handshakeUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-Challenge": challenge
                }
            });

            if (!handshakeResponse.ok) {
                throw new Error(`Handshake API failed with status: ${handshakeResponse.status} `);
            }

            const handshakeData = await handshakeResponse.json();

            // Store the new token
            if (handshakeData.data.token) {
                setAuthToken(handshakeData.data.token);
                setIsLoading(false);
            } else {
                throw new Error("No token received from handshake API");
            }

        } catch (error) {
            console.error("Error refreshing token:", error);
            setAuthToken(null);
            setIsLoading(false);
        }
    };


    const onLikeDislike = async (val: number, index: number) => {
        try {
            const messageId = answers[index][1].message_id;

            if (!messageId) {
                console.error("No message_id found for feedback");
                return;
            }
            const feedbackValue = val === 1 ? 1 : 0;
            // const response = await userFeedback(messageId, feedbackValue, authToken);

            // if (response.ok) {
            //     // Update UI to show feedback was registered
            // } else {
            //     console.error("Feedback submission failed");
            // }
            setAnswers((ans) => {
                const copyAns = [...ans];
                copyAns[index] = [
                    copyAns[index][0],
                    { ...copyAns[index][1], isLike: val },
                    copyAns[index][2]
                ];
                return copyAns;
            });

            // Show success toast
            setFeedbackToastMessage(val === 1 ? "👍 Thanks for your feedback!" : "👎 Thanks for your feedback!");
            setShowFeedbackToast(true);

            // Auto-hide toast after 2 seconds with fade effect
            setTimeout(() => {
                setShowFeedbackToast(false);
            }, 2000);
        } catch (error) {
            console.error("Error submitting feedback:", error);
        }
    }
    const makeApiRequest = async (question: string, options?: { ignoreSession?: boolean }) => {
        lastQuestionRef.current = question;

        // Capture the timestamp when user sends the message
        const userMessageTimestamp = new Date();
        setCurrentUserMessageTimestamp(userMessageTimestamp);

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        if (options?.ignoreSession) {
            setAnswers([]);
        }

        // Immediate scroll when question is sent
        setTimeout(() => {
            if (latestMessageRef.current) {
                latestMessageRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            }
        }, 100);

        try {
            // Check if authentication token is available
            if (!authToken) {
                throw new Error("Authentication token not available. Please wait for initialization.");
            }

            const history: ChatTurn[] = options?.ignoreSession ? [] : answers.map(a => ({ user: a[0], bot: a[1].answer }));
            const request: ChatRequest = {
                history: options?.ignoreSession ? [] : [...history, { user: question, bot: undefined }],
                approach: Approaches.ReadRetrieveRead,
                shouldStream: shouldStream,
                overrides: {
                    promptTemplate: promptTemplate.length === 0 ? undefined : promptTemplate,
                    excludeCategory: excludeCategory.length === 0 ? undefined : excludeCategory,
                    top: retrieveCount,
                    retrievalMode: retrievalMode,
                    semanticRanker: useSemanticRanker,
                    semanticCaptions: useSemanticCaptions,
                    suggestFollowupQuestions: marketingConsent ? useSuggestFollowupQuestions : false,

                },
                username: localStorage.getItem("username"),
                token: authToken,  // Pass the authentication token
                conversationId: options?.ignoreSession ? null : conversationId  // Pass the conversation ID if it exists
            };



            const response = await chatApi(request);


            // Handle different HTTP status codes
            if (!response.ok) {
                let errorMessage = `API request failed with status: ${response.status}`;
                let shouldRetry = false;

                try {
                    const errorData = await response.json();

                    if (response.status === 401) {
                        // Token expired or unauthorized
                        // `detail` may be a string or an object depending on the error type
                        const detailType = errorData?.detail?.type;
                        const detailStr = typeof errorData?.detail === "string" ? errorData.detail : "";

                        if (detailStr.includes("Token expired")) {
                            console.log("Token expired, showing refresh option");
                            setIsTokenExpired(true);
                            setIsLoading(false);
                            return; // Stop processing, let user refresh token
                        } else if (detailType === 'session_ended') {
                            console.log("Session ended, dropping conversation_id and starting fresh");
                            setConversationId(null);
                            setHasHandledConsentThisConversation(false);
                            makeApiRequest(question, { ignoreSession: true });
                            return; // Stop processing the current failed request
                        } else {
                            errorMessage = detailStr || errorData?.message || "Authentication failed. Please refresh and try again.";
                        }
                    } else if (response.status === 403) {
                        errorMessage = "Access denied. You don't have permission to perform this action.";
                    } else if (response.status === 429) {
                        errorMessage = "Too many requests. Please wait a moment and try again.";
                        shouldRetry = true;
                    } else if (response.status >= 500) {
                        errorMessage = "Server error. Please try again in a moment.";
                        shouldRetry = true;
                    } else if (errorData.detail) {
                        errorMessage = errorData.detail;
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                } catch (parseError) {
                    // If we can't parse the error response, use the status code
                    console.error("Error parsing error response:", parseError);
                }

                const error = new Error(errorMessage);
                (error as any).shouldRetry = shouldRetry;
                (error as any).statusCode = response.status;
                throw error;
            }

            // Parse successful response
            const azureResponse = await response.json();
            console.log('Azure API Response:', azureResponse);

            // Check if the response contains an error even with 200 status
            if (azureResponse.error) {
                throw new Error(azureResponse.error);
            }

            const fullMessage = azureResponse.message?.message || "No response received";

            // Validate that we got a meaningful response
            if (!fullMessage || fullMessage.trim() === "") {
                throw new Error("Empty response received from the server");
            }

            // Create response object - CSS will handle typing animation
            const askResponse: AskResponse = {
                answer: fullMessage,
                response_time: new Date(),
                thoughts: null,
                data_points: [],
                error: undefined,
                message_id: azureResponse.conversation_id || undefined,
                ...(azureResponse.message?.follow_up && {
                    follow_up_questions: azureResponse.message.follow_up
                })
            };

            // Store conversation_id for ending conversation later
            if (azureResponse.conversation_id) {
                if (!conversationId) {
                    setHasHandledConsentThisConversation(false); // Reset for new conversation
                    // We call consent API here because this is the first time we get the conversation_id.
                    // This happens after the user has agreed to terms, as they can't chat otherwise.
                    consentApi(azureResponse.conversation_id, marketingConsent, authToken).catch(error => {
                        console.error("Error sending marketing consent:", error);
                    });
                }
                setConversationId(azureResponse.conversation_id);
            }

            // Show marketing consent modal if the API requests it and user hasn't consented yet
            if (
                azureResponse.message?.request_consent === true &&
                !marketingConsent &&
                !showMarketingConsentModal &&
                !hasHandledConsentThisConversation
            ) {
                setShowMarketingConsentModal(true);
                setMarketingConsentChecked(false);
            }


            // Use functional update to avoid stale closure issue
            setAnswers(prevAnswers => {
                const newAnswers: [string, AskResponse, Date][] = [...prevAnswers, [question, askResponse, userMessageTimestamp]];

                // Trigger scroll after state update
                setTimeout(() => {
                    if (latestMessageRef.current) {
                        latestMessageRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "start"
                        });
                    }
                }, 50);

                return newAnswers;
            });

        } catch (e) {
            console.error("Error in makeApiRequest:", e);

            // Handle different types of errors
            // let errorMessage = "An unexpected error occurred. Please try again.";
            let errorMessage = "That didn’t go as planned, but we can retry. Please contact our Helpdesk at 0120-244444 (Monday–Friday, 9:30 AM–6:00 PM).";

            if (e instanceof Error) {
                // Network errors
                if (e.message.includes("fetch")) {
                    console.log("Network error. Please check your connection and try again.");
                    // errorMessage = "Network error. Please check your connection and try again.";
                    errorMessage = "That didn’t go as planned, but we can retry. Please contact our Helpdesk at 0120-244444 (Monday–Friday, 9:30 AM–6:00 PM).";
                } else if (e.message.includes("timeout")) {
                    // errorMessage = "Request timed out. Please try again.";
                    console.log("Request timed out. Please try again.");
                    errorMessage = "That didn’t go as planned, but we can retry. Please contact our Helpdesk at 0120-244444 (Monday–Friday, 9:30 AM–6:00 PM).";
                } else if (e.message.includes("Authentication token not available")) {
                    // errorMessage = "Please wait for the chatbot to initialize and try again.";
                    console.log("Authentication token not available. Please wait for initialization.");
                    errorMessage = "That didn’t go as planned, but we can retry. Please contact our Helpdesk at 0120-244444 (Monday–Friday, 9:30 AM–6:00 PM).";
                } else {
                    errorMessage = e.message;
                    console.log("API error:", errorMessage);
                    errorMessage = "That didn’t go as planned, but we can retry. Please contact our Helpdesk at 0120-244444 (Monday–Friday, 9:30 AM–6:00 PM).";

                }
            }

            // Set the error for display
            setError(new Error(errorMessage));

            // Add error message to chat for better UX
            const errorResponse: AskResponse = {
                answer: `${errorMessage}`,
                response_time: new Date(),
                thoughts: null,
                data_points: [],
                error: errorMessage,
                message_id: undefined
            };

            setAnswers(prevAnswers => [
                ...prevAnswers,
                [question, errorResponse, userMessageTimestamp]
            ]);

        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        setCurrentUserMessageTimestamp(null);
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setConversationId(null);
        setAuthRetryCount(0);
        setIsUnauthorizedBlocked(false);
        setSessionEndedMessage("");
        setHasHandledConsentThisConversation(false);
        clearInactivityTimer(); // Clear inactivity timer when chat is cleared
    };

    // Enhanced auto-scroll functionality
    useEffect(() => {
        // Scroll to question when new answers arrive or loading state changes
        const scrollToLatest = () => {
            if (latestMessageRef.current) {
                latestMessageRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            }
        };

        // Scroll immediately when loading starts (question sent)
        if (isLoading) {
            scrollToLatest();
        }

        // Small delay to ensure DOM is updated
        const timeoutId = setTimeout(scrollToLatest, 100);

        return () => clearTimeout(timeoutId);
    }, [answers, isLoading]);

    // Additional scroll when new message is added
    useEffect(() => {
        if (answers.length > 0) {
            const scrollToLatest = () => {
                if (latestMessageRef.current) {
                    latestMessageRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                }
            };

            // Immediate scroll for new messages
            scrollToLatest();

            // Additional scroll after a short delay to handle dynamic content
            const timeoutId = setTimeout(scrollToLatest, 300);

            return () => clearTimeout(timeoutId);
        }
    }, [answers.length]);

    // Scroll when loading state changes (question appears)
    useEffect(() => {
        if (isLoading && lastQuestionRef.current) {
            const scrollToLatest = () => {
                if (latestMessageRef.current) {
                    latestMessageRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                }
            };

            // Multiple scroll attempts to ensure question is visible
            scrollToLatest();
            setTimeout(scrollToLatest, 50);
            setTimeout(scrollToLatest, 150);
            setTimeout(scrollToLatest, 300);
        }
    }, [isLoading, lastQuestionRef.current]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onRetrievalModeChange = (_ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalMode> | undefined, index?: number | undefined) => {
        setRetrievalMode(option?.data || RetrievalMode.Hybrid);
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            console.log("onShowCitation IF PASSED")
            setActiveAnalysisPanelTab(undefined);
        } else {
            // Use environment variable for production, relative path for dev
            const isDev = import.meta.env.DEV;
            const baseUrl = isDev ? "" : import.meta.env.VITE_CHATBOT_API;
            const url = `${baseUrl}/get_citation/${citation}`;
            window.open(url, "_blank")
        }
        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const saveComment = async () => {
        console.log("comment:", comment)
        if (comment) {
            hidePopup()
            const val = likeVal.current["val"];
            const index = likeVal.current["index"];
            setAnswers((ans) => {
                const copyAns = [...ans];
                copyAns[index] = [
                    copyAns[index][0],
                    { ...copyAns[index][1], isLike: val },
                    copyAns[index][2]
                ];
                return copyAns;
            });
            await update({ isLike: val, id: answers[index][1]["id"], comment: comment }, authToken)
            setComment("")
        } else {
            // commentError.current = "Comment is required"
            setCommentError("Comment is required")
            console.log("commentError:", commentError)
        }
    }

    // Handle marketing consent accept
    const handleMarketingConsentAccept = async () => {
        if (!marketingConsentChecked) return;
        setIsConsentLoading(true);
        try {
            if (conversationId && authToken) {
                await consentApi(conversationId, true, authToken);
            }
            setMarketingConsent(true);
            setShowMarketingConsentModal(false);
            setMarketingConsentChecked(false);
            setHasHandledConsentThisConversation(true);
        } catch (error) {
            console.error("Error accepting marketing consent:", error);
        } finally {
            setIsConsentLoading(false);
        }
    };

    // Handle marketing consent reject
    const handleMarketingConsentReject = () => {
        setShowMarketingConsentModal(false);
        setMarketingConsentChecked(false);
        setHasHandledConsentThisConversation(true);
    };

    const handleRetry = (index: number, question: string) => {
        // Remove the failed message from visual history
        setAnswers(prevAnswers => prevAnswers.filter((_, i) => i !== index));
        // Retry the api request
        makeApiRequest(question);
    };

    return (
        <>
            {/* Floating Toggle Button */}
            <button
                className={`${styles.chatToggleButton} ${!isChatbotOpen || isClosing ? styles.open : ''}`}
                onClick={toggleChatbot}
                aria-label={isChatbotOpen ? "Close chatbot" : "Open chatbot"}
            >
                {(isChatbotOpen && !isClosing) ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ) : (
                    <div className={styles.chatboxButton}>
                        <img src={chatbotLogo} alt="Bot Image" />
                        <div className={styles.jaiWelcomeTextBox}>
                            <span className={styles.jaiTyping}>Hello There!</span>
                        </div>
                        <div className={styles.jaiWelcomeTextBox + " " + styles.jaiWelcomeTextBox2}>
                            <span className={styles.jaiTypingInfinite}>I am JAI</span>
                        </div>
                    </div>
                )}
            </button>

            {/* Chatbot Container */}
            {(isChatbotOpen || isClosing) && (
                <div className={`${styles.container} ${isClosing ? styles.closing : ''} `}>

                    {isPopupVisible && (
                        <Layer>
                            <Popup
                                className={popupStyles.root}
                                role="dialog"
                                aria-modal="true"
                                onDismiss={hidePopup}
                            >
                                <Overlay onClick={hidePopup} />
                                <FocusTrapZone>
                                    <div className={popupStyles.content}>
                                        <h2 className={styles.commentHeader}>Comment</h2>
                                        <div className={styles.commentForm}>
                                            <TextField label="" multiline rows={5} cols={150} value={comment} onChange={(e: any) => {
                                                setComment(e.target.value)
                                                if (e.target.value) {
                                                    // commentError.current = ""
                                                    setCommentError("")
                                                } else {
                                                    // commentError.current = "Comment is required"
                                                    setCommentError("Comment is required")
                                                }

                                            }} errorMessage={commentError} />
                                        </div>
                                        <div>
                                            <DefaultButton onClick={saveComment} primary>Submit</DefaultButton>
                                            <DefaultButton onClick={() => { setComment(""); hidePopup() }} className={styles.closePopup}>Close </DefaultButton>
                                        </div>
                                    </div>
                                </FocusTrapZone>
                            </Popup>
                        </Layer>
                    )}

                    {/* Close Confirmation Popup */}
                    {isCloseConfirmVisible && (
                        <div className={styles.closeConfirmOverlay} onClick={hideCloseConfirm}>
                            <div className={styles.closeConfirmModal} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.closeConfirmHeader}>
                                    <div className={styles.closeConfirmIcon}>
                                        <TiWarning color='#fff' />
                                    </div>
                                    <h2 className={styles.closeConfirmTitle}>End Conversation</h2>
                                </div>
                                <div className={styles.closeConfirmBody}>
                                    <p className={styles.closeConfirmMessage}>
                                        Do you want to end the conversation? This will clear all chat history.
                                    </p>
                                    <div className={`${styles.marketingConsentRow} ${styles.marketingConsentRowBlue} ${!marketingConsent ? styles.marketingConsentRowDisabled : ''}`} style={{ marginTop: '15px' }}>
                                        <div className={styles.marketingConsentCheckboxWrapper}>
                                            <button
                                                type="button"
                                                className={`${styles.svgCheckboxContainer} ${withdrawConsentOnClose ? styles.checked : ''} ${!marketingConsent ? styles.disabled : ''}`}
                                                onClick={() => marketingConsent && setWithdrawConsentOnClose(!withdrawConsentOnClose)}
                                                role="checkbox"
                                                aria-checked={withdrawConsentOnClose}
                                                tabIndex={marketingConsent ? 0 : -1}
                                            >
                                                <svg className={styles.svgCheckboxIcon} viewBox="0 0 24 24">
                                                    <path d="M20 6L9 17L4 12" />
                                                </svg>
                                            </button>
                                            <span className={styles.checkboxLabelText}>
                                                I withdraw my consent for data processing.
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`${styles.marketingConsentRow} ${styles.marketingConsentRowBlue}`} style={{ marginTop: '10px' }}>
                                        <div className={styles.marketingConsentCheckboxWrapper}>
                                            <div
                                                // type="button"
                                                className={`${styles.svgCheckboxContainer} ${deleteDataOnClose ? styles.checked : ''}`}
                                                onClick={() => setDeleteDataOnClose(!deleteDataOnClose)}
                                                role="checkbox"
                                                aria-checked={deleteDataOnClose}
                                                tabIndex={0}
                                            >
                                                <svg className={styles.svgCheckboxIcon} viewBox="0 0 24 24">
                                                    <path d="M20 6L9 17L4 12" />
                                                </svg>
                                            </div>
                                            <span className={styles.checkboxLabelText}>
                                                I request the deletion of my personal data, subject to applicable legal requirements.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.closeConfirmFooter}>
                                    <button
                                        className={`${styles.closeConfirmButton} ${styles.closeConfirmButtonCancel}`}
                                        onClick={hideCloseConfirm}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`${styles.closeConfirmButton} ${styles.closeConfirmButtonConfirm}`}
                                        onClick={handleEndConversation}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Privacy Notice Popup */}
                    {isPrivacyPolicyVisible && (
                        <div className={styles.privacyPolicyOverlay}>
                            <div className={styles.privacyPolicyModal} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.privacyPolicyHeader}>
                                    <div className={styles.privacyPolicyIcon}>
                                        <MdPrivacyTip color='#fff' />
                                    </div>
                                    <h2 className={styles.privacyPolicyTitle}>Privacy Notice</h2>
                                </div>
                                <div className={styles.privacyPolicyBody}>
                                    <div className={`${styles.marketingConsentRow} mt-0`} style={{ marginBottom: '8px' }}>
                                        <div className={styles.marketingConsentCheckboxWrapper}>
                                            <button
                                                type="button"
                                                className={`${styles.svgCheckboxContainer} ${hasAgreedToPrivacyNotice ? styles.checked : ''}`}
                                                onClick={() => setHasAgreedToPrivacyNotice(!hasAgreedToPrivacyNotice)}
                                                role="checkbox"
                                                aria-checked={hasAgreedToPrivacyNotice}
                                                tabIndex={0}
                                            >
                                                <svg className={styles.svgCheckboxIcon} viewBox="0 0 24 24">
                                                    <path d="M20 6L9 17L4 12" />
                                                </svg>
                                            </button>
                                            <span className={styles.checkboxLabelText}>
                                                By clicking the checkbox, I confirm that I have read and understand the Jindal Panther{' '}
                                                <a
                                                    href={`${import.meta.env.VITE_MAIN_API}/privacy-policy`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.privacyPolicyLink}
                                                >
                                                    Privacy Notice
                                                </a>.
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`${styles.marketingConsentRow} ${!hasAgreedToPrivacyNotice ? styles.marketingConsentRowDisabled : ''}`}>
                                        <div className={styles.marketingConsentCheckboxWrapper}>
                                            <button
                                                type="button"
                                                className={`${styles.svgCheckboxContainer} ${marketingConsent ? styles.checked : ''} ${!hasAgreedToPrivacyNotice ? styles.disabled : ''}`}
                                                onClick={() => hasAgreedToPrivacyNotice && setMarketingConsent(!marketingConsent)}
                                                role="checkbox"
                                                aria-checked={marketingConsent}
                                                tabIndex={hasAgreedToPrivacyNotice ? 0 : -1}
                                                disabled={!hasAgreedToPrivacyNotice}
                                            >
                                                <svg className={styles.svgCheckboxIcon} viewBox="0 0 24 24">
                                                    <path d="M20 6L9 17L4 12" />
                                                </svg>
                                            </button>
                                            <span className={styles.checkboxLabelText}>
                                                By checking this box, I consent to being contacted with information about products, services, and offers.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.privacyPolicyFooter}>
                                    <button
                                        className={`${styles.privacyPolicyButton} ${styles.privacyPolicyButtonAgree}`}
                                        onClick={handlePrivacyPolicyAgree}
                                        disabled={!hasAgreedToPrivacyNotice}
                                    >
                                        {marketingConsent ? "Continue" : "Continue Anonymously"}
                                    </button>
                                    <div className={styles.privacyPolicyDisclaimer}>
                                        <span className={styles.privacyPolicyDisclaimerIcon}><MdOutlineInfo /></span>
                                        <span className={styles.privacyPolicyDisclaimerText}>
                                            Responses are AI-generated. User discreation is advised.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Marketing Consent Modal */}
                    {showMarketingConsentModal && (
                        <div className={styles.privacyPolicyOverlay}>
                            <div className={styles.privacyPolicyModal} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.privacyPolicyHeader}>
                                    <div className={styles.privacyPolicyIcon}>
                                        <MdPrivacyTip color='#fff' />
                                    </div>
                                    <h2 className={styles.privacyPolicyTitle}>Marketing Consent</h2>
                                </div>
                                <div className={styles.privacyPolicyBody}>
                                    <div className={styles.marketingConsentRow}>
                                        <div className={styles.marketingConsentCheckboxWrapper}>
                                            <button
                                                type="button"
                                                className={`${styles.svgCheckboxContainer} ${marketingConsentChecked ? styles.checked : ''}`}
                                                onClick={() => setMarketingConsentChecked(!marketingConsentChecked)}
                                                role="checkbox"
                                                aria-checked={marketingConsentChecked}
                                                tabIndex={0}
                                            >
                                                <svg className={styles.svgCheckboxIcon} viewBox="0 0 24 24">
                                                    <path d="M20 6L9 17L4 12" />
                                                </svg>
                                            </button>
                                            <span className={styles.checkboxLabelText}>
                                                By checking this box, I consent to being contacted with information about products, services, and offers.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.privacyPolicyFooter}>
                                    <div className={styles.marketingConsentModalActions}>
                                        <button
                                            className={`${styles.marketingConsentActionBtn} ${styles.marketingConsentRejectBtn}`}
                                            onClick={handleMarketingConsentReject}
                                            disabled={isConsentLoading}
                                        >
                                            Reject
                                        </button>
                                        <button
                                            className={`${styles.marketingConsentActionBtn} ${styles.marketingConsentAcceptBtn}`}
                                            onClick={handleMarketingConsentAccept}
                                            disabled={!marketingConsentChecked || isConsentLoading}
                                        >
                                            {isConsentLoading ? 'Saving...' : 'Accept'}
                                        </button>
                                    </div>
                                    <div className={styles.privacyPolicyDisclaimer}>
                                        <span className={styles.privacyPolicyDisclaimerIcon}><MdOutlineInfo /></span>
                                        <span className={styles.privacyPolicyDisclaimerText}>
                                            Responses are AI-generated. User discreation is advised.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.chatHeader}>
                        <div className={styles.chatHeaderTitle}>
                            <img src={headerLogo} alt="Ask JAI" />
                        </div>
                        <div className={styles.chatHeaderActions}>
                            <img src={headerImage} alt="" />
                        </div>
                    </div>
                    <div className={styles.chatRoot}>
                        <div className={styles.chatContainer}>
                            {!lastQuestionRef.current ? (
                                <div className={styles.chatEmptyState}>
                                    <div className={styles.classFier}>

                                        <div className={styles.staticlogojaiicon}>
                                            <img src={logo} alt="Ask JAI" width="25px;" />
                                        </div>
                                        <div className={styles.StaticGroup}>
                                            <div className={styles.Templatecontainer}>
                                                <div className={styles.TemplateQuery}>
                                                    <span className={styles.TemplateQuery1}>Hello! Thank you for reaching out to Jindal Steel.</span>
                                                </div>
                                            </div>

                                            <div className={styles.Triangle}>
                                                <img src={triangle} alt="Ask JAI" width="25px;" height="35px;"></img>
                                            </div>

                                            <div className={styles.Templatecontainer2}>
                                                <div className={styles.TemplateQuery}>
                                                    <span className={styles.TemplateQuery3}>We offer world-class Steel and Cement products trusted for residential, commercial, and infrastructure development. How can I assist you?</span>
                                                    {/* <span className={styles.TemplateQuery4}>Eg. Loyalty programs, Prices, Sales offices, Distributors & Dealers for TMT in your location.</span> */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            ) : (

                                <div className={styles.chatMessageStream}>
                                    <div className={styles.chatEmptyState}>
                                        <div className={styles.classFier}>

                                            <div className={styles.staticlogojaiicon}>
                                                <img src={logo} alt="Ask JAI" width="25px;" />
                                            </div>

                                            <div className={styles.StaticGroup}>
                                                <div className={styles.Templatecontainer3}>
                                                    <div className={styles.TemplateQuery}>
                                                        <span className={styles.TemplateQuery1}>Hello! Thank you for reaching out to Jindal Steel.</span>
                                                    </div>
                                                </div>

                                                <div className={styles.Triangle2}>
                                                    <img src={triangle} alt="Ask JAI" width="25px;" height="35px;"></img>
                                                </div>

                                                <div className={styles.Templatecontainer2}>
                                                    <div className={styles.TemplateQuery}>
                                                        {/* <span className={styles.TemplateQuery3}>You can ask me TMT related queries.</span> */}
                                                        <span className={styles.TemplateQuery3}>We offer world-class Steel and Cement products trusted for residential, commercial, and infrastructure development. How can I assist you?</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {answers.map((answer, index) => {
                                        const isLatest = index === answers.length - 1 && !isLoading;
                                        return (
                                            <div key={index} ref={isLatest ? latestMessageRef : null}>
                                                <UserChatMessage message={answer[0]} timestamp={answer[2]} />
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        key={index}
                                                        answer={answer[1]}
                                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                        onCitationClicked={c => onShowCitation(c, index)}
                                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                        onLikeDislikeClicked={(val) => {
                                                            onLikeDislike(val, index)
                                                        }}
                                                        showFollowupQuestions={marketingConsent ? useSuggestFollowupQuestions : false}
                                                        onFollowupsShown={() => {
                                                            // Do nothing here to prevent scrolling to bottom
                                                        }}
                                                        onRetryClicked={() => handleRetry(index, answer[0])}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {isLoading && (
                                        <div ref={latestMessageRef}>
                                            <UserChatMessage message={lastQuestionRef.current} timestamp={currentUserMessageTimestamp || undefined} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <AnswerLoading />
                                            </div>
                                        </div>
                                    )}

                                    {/* Token Expired Message */}

                                    {isTokenExpired && (
                                        <div className={styles.tokenExpiredContainer}>
                                            <div className={styles.tokenExpiredMessage}>
                                                <span className={styles.tokenExpiredIcon}><TiWarning color='#856404' /></span>
                                                <h3>Session Expired</h3>
                                                <p>Your session has expired. Please start a new chat to continue.</p>
                                                <button
                                                    className={styles.refreshTokenButton}
                                                    onClick={refreshToken}
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? "Refreshing..." : "Start New Chat"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div ref={chatMessageStreamEnd} />
                                </div>

                            )
                            }

                            {/* Session Ended (Unauthorized) Popup - outside the conditional so it always renders */}
                            {isUnauthorizedBlocked && (
                                <div className={styles.tokenExpiredContainer}>
                                    <div className={styles.tokenExpiredMessage}>
                                        <span className={styles.tokenExpiredIcon}><TiWarning color='#856404' /></span>
                                        <h3>Verification Failed</h3>
                                        <p>{sessionEndedMessage || "You have exceeded the maximum number of attempts. Your session has been ended."}</p>
                                    </div>
                                </div>
                            )}

                        </div>


                        {answers.length > 0 && activeAnalysisPanelTab && (
                            <AnalysisPanel
                                className={styles.chatAnalysisPanel}
                                activeCitation={activeCitation}
                                onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                                citationHeight="810px"
                                answer={answers[selectedAnswer][1]}
                                activeTab={activeAnalysisPanelTab}
                            />
                        )}

                        <Panel
                            headerText="Configure answer generation"
                            isOpen={isConfigPanelOpen}
                            isBlocking={false}
                            onDismiss={() => setIsConfigPanelOpen(false)}
                            closeButtonAriaLabel="Close"
                            onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                            isFooterAtBottom={true}
                        >
                            <TextField
                                className={styles.chatSettingsSeparator}
                                defaultValue={promptTemplate}
                                label="Override prompt template"
                                multiline
                                autoAdjustHeight
                                onChange={onPromptTemplateChange}
                            />

                            <SpinButton
                                className={styles.chatSettingsSeparator}
                                label="Retrieve this many search results:"
                                min={1}
                                max={50}
                                defaultValue={retrieveCount.toString()}
                                onChange={onRetrieveCountChange}
                            />
                            <TextField className={styles.chatSettingsSeparator} label="Exclude category" onChange={onExcludeCategoryChanged} />
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={useSemanticRanker}
                                label="Use semantic ranker for retrieval"
                                onChange={onUseSemanticRankerChange}
                            />
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={useSemanticCaptions}
                                label="Use query-contextual summaries instead of whole documents"
                                onChange={onUseSemanticCaptionsChange}
                                disabled={!useSemanticRanker}
                            />
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={useSuggestFollowupQuestions}
                                label="Suggest follow-up questions"
                                onChange={onUseSuggestFollowupQuestionsChange}
                            />
                            <Dropdown
                                className={styles.chatSettingsSeparator}
                                label="Retrieval mode"
                                options={[
                                    { key: "hybrid", text: "Vectors + Text (Hybrid)", selected: retrievalMode == RetrievalMode.Hybrid, data: RetrievalMode.Hybrid },
                                    { key: "vectors", text: "Vectors", selected: retrievalMode == RetrievalMode.Vectors, data: RetrievalMode.Vectors },
                                    { key: "text", text: "Text", selected: retrievalMode == RetrievalMode.Text, data: RetrievalMode.Text }
                                ]}
                                required
                                onChange={onRetrievalModeChange}
                            />
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={shouldStream}
                                label="Stream chat completion responses"
                                onChange={onShouldStreamChange}
                            />
                        </Panel>
                    </div>
                    <div className={styles.chatInput} style={activeAnalysisPanelTab ? { width: "50%" } : {}}>
                        <QuestionInput
                            clearOnSend
                            placeholder={isUnauthorizedBlocked ? "Session ended. Please close and reopen to start a new chat." : "How may i help you? (e.g. What is the price of TMT 8 mm in Delhi? )"}
                            disabled={isLoading || isUnauthorizedBlocked || showMarketingConsentModal}
                            onSend={question => makeApiRequest(question.trim())}
                        />
                    </div>
                    {/* Feedback Success Toast */}
                    {showFeedbackToast && (
                        <div className={styles.feedbackToast}>
                            <div className={styles.feedbackToastContent}>
                                <span>{feedbackToastMessage || "Feedback submitted successfully"}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

        </>

    );
};

export default Chat;
